/*
 * Dynamic delivery block – vanilla JS, timezone-aware dispatch & countdown
 */
(function () {
  const instances = new WeakMap()

  const WEEKDAY_MAP = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }

  function parseCutoff (value) {
    const source = String(value || '14:00').trim()
    const match12 = source.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)

    if (match12) {
      let hour = parseInt(match12[1], 10)
      const minute = parseInt(match12[2], 10)
      const isPm = match12[3].toUpperCase() === 'PM'

      if (hour === 12) hour = isPm ? 12 : 0
      else if (isPm) hour += 12

      return { hour, minute }
    }

    const match24 = source.match(/^(\d{1,2}):(\d{2})$/)

    if (match24) {
      return {
        hour: parseInt(match24[1], 10),
        minute: parseInt(match24[2], 10)
      }
    }

    return { hour: 14, minute: 0 }
  }

  function getZonedParts (date, timeZone) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      weekday: 'short',
      hour12: false
    })
    const parts = {}

    formatter.formatToParts(date).forEach(({ type, value }) => {
      if (type !== 'literal') parts[type] = value
    })

    if (parts.hour === '24') parts.hour = '00'

    return parts
  }

  function getOffsetMs (timeZone, date) {
    const parts = getZonedParts(date, timeZone)
    const asUtc = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute),
      Number(parts.second)
    )

    return asUtc - date.getTime()
  }

  function zonedTimeToUtc (year, month, day, hour, minute, second, timeZone) {
    const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second)
    const offset = getOffsetMs(timeZone, new Date(utcGuess))

    return new Date(utcGuess - offset)
  }

  function addCalendarDays (year, month, day, days) {
    const date = new Date(Date.UTC(year, month - 1, day + days))

    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate()
    }
  }

  function getWeekdayIndex (year, month, day, timeZone) {
    const probe = zonedTimeToUtc(year, month, day, 12, 0, 0, timeZone)
    const weekday = getZonedParts(probe, timeZone).weekday

    return WEEKDAY_MAP[weekday]
  }

  function getDispatchDays (config) {
    const dayMap = {
      sun: 0,
      mon: 1,
      tue: 2,
      wed: 3,
      thu: 4,
      fri: 5,
      sat: 6
    }

    if (Array.isArray(config.dispatchDays)) {
      return config.dispatchDays
    }

    return String(config.dispatchDays || '')
      .toLowerCase()
      .split(/[,\s]+/)
      .map((token) => dayMap[token.trim().slice(0, 3)])
      .filter((day) => day !== undefined)
  }

  function computeSchedule (config) {
    const now = new Date()
    const timeZone = config.timezone
    const cutoff = parseCutoff(config.cutoffTime)
    const dispatchDays = getDispatchDays(config)
    const today = getZonedParts(now, timeZone)
    const startYear = Number(today.year)
    const startMonth = Number(today.month)
    const startDay = Number(today.day)

    for (let offset = 0; offset < 14; offset += 1) {
      const dispatchDate = addCalendarDays(startYear, startMonth, startDay, offset)
      const weekday = getWeekdayIndex(dispatchDate.year, dispatchDate.month, dispatchDate.day, timeZone)

      if (!dispatchDays.includes(weekday)) continue

      const cutoffUtc = zonedTimeToUtc(
        dispatchDate.year,
        dispatchDate.month,
        dispatchDate.day,
        cutoff.hour,
        cutoff.minute,
        0,
        timeZone
      )

      if (now.getTime() >= cutoffUtc.getTime()) continue

      let deliveryDate = addCalendarDays(
        dispatchDate.year,
        dispatchDate.month,
        dispatchDate.day,
        config.transitDays
      )
       if (getWeekdayIndex(deliveryDate.year, deliveryDate.month, deliveryDate.day, timeZone) === 0) {
        deliveryDate = addCalendarDays(deliveryDate.year, deliveryDate.month, deliveryDate.day, 1)
      }
      const deliveryUtc = zonedTimeToUtc(
        deliveryDate.year,
        deliveryDate.month,
        deliveryDate.day,
        12,
        0,
        0,
        timeZone
      )

      return { cutoffUtc, deliveryUtc }
    }

    return null
  }

  function formatCountdown (remainingMs, labels) {
    const totalMinutes = Math.max(0, Math.ceil(remainingMs / 60000))

    if (totalMinutes < 60) {
      const unit = totalMinutes === 1 ? labels.minute : labels.minutes
      return `${totalMinutes} ${unit}`
    }

    const totalHours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60

    if (totalHours < 24) {
      const hourUnit = totalHours === 1 ? labels.hour : labels.hours

      if (minutes === 0) return `${totalHours} ${hourUnit}`

      const minuteUnit = minutes === 1 ? labels.minute : labels.minutes
      return `${totalHours} ${hourUnit} ${minutes} ${minuteUnit}`
    }

    const days = Math.floor(totalHours / 24)
    const hours = totalHours % 24
    const dayUnit = days === 1 ? labels.day : labels.days

    if (hours === 0) return `${days} ${dayUnit}`

    const hourUnit = hours === 1 ? labels.hour : labels.hours
    return `${days} ${dayUnit} ${hours} ${hourUnit}`
  }

  function formatDeliveryDate (date, locale, timeZone) {
    const nowParts = getZonedParts(new Date(), timeZone)
    const dateParts = getZonedParts(date, timeZone)
    const options = {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      timeZone
    }

    if (dateParts.year !== nowParts.year) {
      options.year = 'numeric'
    }

    // Force UK-style order: "Thursday 10 September" (not "Thursday September 10")
    const parts = new Intl.DateTimeFormat(locale || 'en-GB', options).formatToParts(date)
    const byType = {}

    parts.forEach(({ type, value }) => {
      byType[type] = value
    })

    const segments = [byType.weekday, byType.day, byType.month]
    if (byType.year) segments.push(byType.year)

    return segments.filter(Boolean).join(' ')
  }

  function replaceToken (template, token, value) {
    const pattern = new RegExp(`\\[${escapeRegExp(token)}\\]`, 'gi')
    return String(template || '').replace(pattern, value == null ? '' : String(value))
  }

  function escapeRegExp (value) {
    return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }

  function applyLabelTokens (template, tokens) {
    return Object.keys(tokens).reduce((text, key) => {
      return replaceToken(text, key, tokens[key])
    }, String(template || ''))
  }

  function toCents (value) {
    const amount = Number(value)
    return Number.isFinite(amount) ? amount : 0
  }

  function qualifiesForFreeDelivery (config) {
    if (!config.showPricing) return false

    const thresholdCents = toCents(config.thresholdCents)
    if (thresholdCents <= 0) return false

    // Order qualifies if cart total OR this product alone meets the free-delivery threshold
    const orderAmountCents = Math.max(
      toCents(config.cartTotalCents),
      toCents(config.currentPriceCents)
    )

    return orderAmountCents >= thresholdCents
  }

  function getFreeTemplate (config, qualifies) {
    return qualifies
      ? 'Free delivery [date].'
      : 'Free delivery [date] on orders over [threshold].'
  }

  function getCountdownTemplate (config, qualifies) {
    if (qualifies || !config.showPricing) {
      return 'Order within [countdown].'
    }

    return 'Order within [countdown]. [cost] under [threshold].'
  }
function stripMoneyHtml (value) {
  return String(value || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
}
  function createTextEl (text, className, color) {
    const el = document.createElement('span')
    if (className) el.className = className
    el.textContent = text
    if (color) el.style.color = color
    return el
  }

  function createEmphasisEl (text, className, color) {
    const el = document.createElement('strong')
    el.className = className
    el.textContent = text
    if (color) el.style.color = color
    return el
  }

  function findTokenIndex (template, token) {
    const match = String(template || '').match(new RegExp(`\\[${escapeRegExp(token)}\\]`, 'i'))
    if (!match) return { index: -1, tokenText: `[${token}]` }
    return { index: match.index, tokenText: match[0] }
  }

  function renderFreeLine (lineEl, template, dateStr, options) {
    if (!lineEl) return

    const templateText = applyLabelTokens(template, options.tokens || {})
    const { index: tokenIndex, tokenText } = findTokenIndex(templateText, 'date')

    lineEl.textContent = ''
    lineEl.setAttribute('data-qualifies', options.qualifies ? 'true' : 'false')

    if (tokenIndex === -1) {
      lineEl.appendChild(document.createTextNode(replaceToken(templateText, 'date', dateStr)))
      return
    }

    const before = templateText.slice(0, tokenIndex)
    const after = templateText.slice(tokenIndex + tokenText.length)

    // "Free delivery" stays regular weight; only the date is bold
    if (before) {
      lineEl.appendChild(createTextEl(before, 'dynamic-delivery__label'))
    }

    lineEl.appendChild(
      createEmphasisEl(dateStr, 'dynamic-delivery__date', options.dateColor)
    )

    if (after) {
      lineEl.appendChild(document.createTextNode(after))
    }
  }

 function renderCountdownLine (lineEl, countdownText, options) {
  if (!lineEl) return

  lineEl.textContent = ''
  lineEl.classList.remove('is-urgent')
  lineEl.style.removeProperty('--dd-urgent-color')

  // Always build suffix from settings (not from template parsing)
  let suffix = ''
  if (!options.qualifies && options.showPricing) {
    const cost = stripMoneyHtml(options.tokens?.cost)
    const threshold = stripMoneyHtml(options.tokens?.threshold)
    if (cost && threshold) {
      suffix = ` ${cost} under ${threshold}.`
    } else if (threshold) {
      suffix = ` under ${threshold}.`
    }
  }

  if (options.isUrgent) {
    const urgentEl = createTextEl(
      `Order within ${countdownText}.`,
      'dynamic-delivery__countdown-urgent'
    )
    if (options.urgentColor) urgentEl.style.color = options.urgentColor
    lineEl.appendChild(urgentEl)
  } else {
    lineEl.appendChild(document.createTextNode('Order within '))
    lineEl.appendChild(
      createTextEl(countdownText, 'dynamic-delivery__countdown', options.countdownColor)
    )
    lineEl.appendChild(document.createTextNode('.'))
  }

  if (suffix) {
    lineEl.appendChild(document.createTextNode(suffix))
  }

  lineEl.setAttribute('data-has-suffix', suffix ? 'true' : 'false')
  lineEl.setAttribute('data-qualifies', options.qualifies ? 'true' : 'false')
}

  function destroyInstance (root) {
    const state = instances.get(root)

    if (state?.intervalId) clearInterval(state.intervalId)
    if (state?.onVariantChange) {
      window.removeEventListener('variantChange.ecomify.product', state.onVariantChange)
    }
    if (state?.onCartUpdated) {
      window.removeEventListener('updated.ecomify.cart', state.onCartUpdated)
    }
    if (state?.onVariantAdd) {
      document.removeEventListener('variant:add', state.onVariantAdd)
    }
    if (state?.onLocalSync) {
      root.removeEventListener('dynamic-delivery:sync-cart', state.onLocalSync)
    }
    instances.delete(root)
  }

  function getCartJsUrl () {
    const root = window.Shopify?.routes?.root || '/'
    return `${root}cart.js`
  }

  async function refreshCartTotal (config) {
    try {
      const cart = await (await fetch(getCartJsUrl(), { credentials: 'same-origin' })).json()
      if (cart && cart.total_price != null) {
        config.cartTotalCents = toCents(cart.total_price)
      }
    } catch (error) {
      // Keep the last known cart total if the request fails
    }

    return config.cartTotalCents
  }

  function applyCartTotal (config, cartLike) {
    if (!cartLike || cartLike.total_price == null) return false
    config.cartTotalCents = toCents(cartLike.total_price)
    return true
  }

  function initInstance (root) {
    destroyInstance(root)

    const configNode = root.querySelector('[data-dynamic-delivery-config]')

    if (!configNode) return

    let config

    try {
      config = JSON.parse(configNode.textContent)
    } catch (error) {
      root.hidden = true
      return
    }

    if (!config.enabled) {
      root.hidden = true
      return
    }

    config.cartTotalCents = toCents(config.cartTotalCents)
    config.currentPriceCents = toCents(config.currentPriceCents)

    const refs = {
      freeLine: root.querySelector('[data-free-line]'),
      countdownLine: root.querySelector('[data-countdown-line]'),
      detailsWrap: root.querySelector('[data-details]')
    }

    function update () {
      const schedule = computeSchedule(config)

      if (!schedule) {
        root.classList.remove('is-ready')
        root.hidden = true
        return
      }

      const remaining = schedule.cutoffUtc.getTime() - Date.now()
      const deliveryDate = formatDeliveryDate(schedule.deliveryUtc, config.locale, config.timezone)
      const countdownText = formatCountdown(remaining, config.countdownLabels)
      const isUrgent = remaining <= (config.urgentThresholdMinutes * 60000)
      const qualifies = qualifiesForFreeDelivery(config)
      const labelTokens = {
        threshold: config.thresholdFormatted || '',
        cost: config.costFormatted || ''
      }

      if (refs.freeLine && config.showPricing) {
        renderFreeLine(refs.freeLine, getFreeTemplate(config, qualifies), deliveryDate, {
          tokens: labelTokens,
          dateColor: config.dateColor,
          qualifies
        })
      }

  renderCountdownLine(
  refs.countdownLine,
  countdownText,
  {
    tokens: labelTokens,
    isUrgent,
    urgentColor: config.urgentColor,
    countdownColor: config.countdownColor,
    qualifies,
    showPricing: config.showPricing
  }
)

      if (refs.detailsWrap) {
        refs.detailsWrap.hidden = false
      }

      root.dataset.qualifies = qualifies ? 'true' : 'false'
      root.dataset.cartTotalCents = String(toCents(config.cartTotalCents))
      root.dataset.priceCents = String(toCents(config.currentPriceCents))
      root.dataset.thresholdCents = String(toCents(config.thresholdCents))
      root.classList.add('is-ready')
      root.hidden = false
    }

    async function syncCartAndUpdate (cartLike) {
      if (!applyCartTotal(config, cartLike)) {
        await refreshCartTotal(config)
      }
      update()
    }

    const onVariantChange = (event) => {
      const variant = event.detail

      if (!variant || variant.price == null) return

      config.currentPriceCents = toCents(variant.price)
      update()
    }

    const onCartUpdated = () => {
      syncCartAndUpdate()
    }

    const onVariantAdd = (event) => {
      syncCartAndUpdate(event.detail?.cart)
    }

    const onLocalSync = (event) => {
      syncCartAndUpdate(event.detail?.cart)
    }

    update()
    syncCartAndUpdate()

    const intervalId = window.setInterval(update, 60000)
    window.addEventListener('variantChange.ecomify.product', onVariantChange)
    window.addEventListener('updated.ecomify.cart', onCartUpdated)
    document.addEventListener('variant:add', onVariantAdd)
    root.addEventListener('dynamic-delivery:sync-cart', onLocalSync)
    instances.set(root, {
      intervalId,
      onVariantChange,
      onCartUpdated,
      onVariantAdd,
      onLocalSync
    })
  }

  function hookCartRefresh () {
    if (window.__dynamicDeliveryCartHooked) return
    window.__dynamicDeliveryCartHooked = true

    const triggerSync = () => {
      document.querySelectorAll('[data-dynamic-delivery]').forEach((root) => {
        root.dispatchEvent(new CustomEvent('dynamic-delivery:sync-cart', { detail: {} }))
      })
    }

    const wrapRefresh = () => {
      if (typeof window.refreshCartContents !== 'function') return false
      if (window.refreshCartContents.__dynamicDeliveryWrapped) return true

      const originalRefresh = window.refreshCartContents

      window.refreshCartContents = async function wrappedRefreshCartContents (...args) {
        const result = await originalRefresh.apply(this, args)
        triggerSync()
        return result
      }

      window.refreshCartContents.__dynamicDeliveryWrapped = true
      return true
    }

    if (!wrapRefresh()) {
      let attempts = 0
      const timer = window.setInterval(() => {
        attempts += 1
        if (wrapRefresh() || attempts > 40) clearInterval(timer)
      }, 250)
    }

    if (!window.__dynamicDeliveryFetchHooked && typeof window.fetch === 'function') {
      window.__dynamicDeliveryFetchHooked = true
      const originalFetch = window.fetch.bind(window)

      window.fetch = async function patchedFetch (input, init) {
        const response = await originalFetch(input, init)
        const url = String(typeof input === 'string' ? input : input?.url || '')

        if (/\/cart\/(add|change|update|clear)/.test(url)) {
          window.setTimeout(triggerSync, 150)
        }

        return response
      }
    }
  }

  function initScope (scope) {
    if (!scope || !scope.querySelectorAll) return

    hookCartRefresh()
    scope.querySelectorAll('[data-dynamic-delivery]').forEach(initInstance)
  }

  function unloadScope (scope) {
    if (!scope || !scope.querySelectorAll) return

    scope.querySelectorAll('[data-dynamic-delivery]').forEach(destroyInstance)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initScope(document))
  } else {
    initScope(document)
  }

  document.addEventListener('shopify:section:load', (event) => {
    initScope(event.target)
  })

  document.addEventListener('shopify:section:unload', (event) => {
    unloadScope(event.target)
  })
})()
